/**
 * This file should be the one public interface to interact with Auth from the wider Fab3
 */
import { useMemo } from "react";
import { SSOImplementation, getSSOImplementation } from "./auth";
import { useSpaSSO } from "./new-msal/store";
import { useExternalSSO } from "./external";

export type Auth = {
  /** string enum representing the sso implementation in use */
  ssoImplementation: SSOImplementation;

  /** gets a token async-ly, refreshes if need be to always return a fresh token */
  getAccessToken: () => Promise<string>;

  /**
   * cached JWT that follows the primary access token. never use this for API calls, just for
   * rendering UI that needs to know about the user's claims
   */
  renderingToken: string;

  /** force-refreshes the access token, return types are inconsistent */
  refreshAccessToken: (correlationId?: string) => Promise<string | boolean>;

  /** sets the correlation ID for the current session, and refreshes the token */
  setCorrelationId?: (correlationId: string) => Promise<void>;
  /** unsets the correlation ID for the current session, and refreshes the token */
  unsetCorrelationId?: () => Promise<void>;

  /** logout :3 */
  logout: () => Promise<void> | void;
};

/**
 * Gives access to the initialized auth info for whichever SSO implementation is in use. Must be
 * called inside the AuthGuard.
 */
export const useAuth = (): Auth => {
  const ssoImplementation = getSSOImplementation();

  const spaSSO = useSpaSSO();
  const externalSSO = useExternalSSO();

  return useMemo(() => {
    switch (ssoImplementation) {
      case "spaSSO": {
        if (!spaSSO) {
          throw new Error(
            "Can't use spaSSO before it's initialized. Are you calling useAuth inside the AuthGuard?",
          );
        }
        return spaSSO;
      }
      case "externalSSO": {
        if (!externalSSO) {
          throw new Error(
            "Can't use externalSSO before it's initialized. Are you calling useAuth inside the AuthGuard?",
          );
        }
        return externalSSO;
      }
      default: {
        throw new Error(
          `Unsupported SSO implementation: ${ssoImplementation as string}`,
        );
      }
    }
  }, [ssoImplementation, spaSSO, externalSSO]);
};

/** Returns a stable async function that always provides a fresh token. */
export const useAccessToken = (): (() => Promise<string>) => {
  const auth = useAuth();
  return auth.getAccessToken;
};
